import React from 'react';
import { Chip, type ChipProps } from '@jotpsych/ui';
import { Box, Stack, Typography } from '@mui/material';

import type { TextBlockData } from '../../types';
import { ActionList } from '../ActionList';

const TAG_VARIANT_COLORS: Record<string, ChipProps['color']> = {
  improving: 'success',
  resolved: 'primary',
  worsening: 'error',
  not_at_goal: 'warning',
  stable: 'default',
  new: 'info',
};

type TextBlockRendererProps = {
  typeKey: string;
  data: TextBlockData;
};

export const TextBlockRenderer: React.FC<TextBlockRendererProps> = ({ typeKey, data }) => {
  const { title, subtitle, tags, body, actions } = data;
  const hasTags = tags && tags.length > 0;
  const hasActions = actions && actions.length > 0;

  if (!title && !subtitle && !hasTags && !body && !hasActions) return null;

  return (
    <Box
      sx={{
        border: 1,
        borderColor: 'grey.200',
        borderRadius: 2,
        p: '16px 20px',
      }}
    >
      <Stack spacing={1}>
        {title && (
          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
            {title}
          </Typography>
        )}

        {subtitle && (
          <Typography variant="body2" color="text.secondary">
            {subtitle}
          </Typography>
        )}

        {hasTags && (
          <Stack direction="row" spacing={0.75} useFlexGap role="group" aria-label="Status tags" sx={{ flexWrap: "wrap" }}>
            {tags.map((tag, i) => (
              <Chip
                key={tag.label ?? i}
                label={tag.label}
                size="small"
                variant="soft"
                color={tag.variant ? TAG_VARIANT_COLORS[tag.variant] : 'default'}
              />
            ))}
          </Stack>
        )}

        {body && (
          <Typography variant="body2" color="text.secondary">
            {body}
          </Typography>
        )}

        {hasActions && <ActionList actions={actions} typeKey={typeKey} />}
      </Stack>
    </Box>
  );
};
